import { createFileRoute, Link, Navigate, notFound } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { ArrowRight, CheckCircle2, ChevronLeft, Circle, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import {
  canAccessModule,
  findLesson,
  modules,
  TIER_LABEL,
  TIER_RANK,
  type AcademyTier,
} from "@/lib/academy";
import { useLessonProgress } from "@/hooks/useLessonProgress";
import { getAcademyLessonOverride } from "@/lib/academy-lessons";
import { KeyTakeaways } from "@/components/site/article/KeyTakeaways";
import { FAQ } from "@/components/site/article/FAQ";
import { LessonReaderSidebar } from "@/components/dashboard/academy/LessonReaderSidebar";

export const Route = createFileRoute("/_authenticated/dashboard/academy/$slug")({
  loader: ({ params }) => {
    const found = findLesson(params.slug);
    if (!found) throw notFound();
    return { slug: params.slug };
  },
  component: LessonReader,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Lesson not found</h1>
      <Link
        to="/dashboard/academy"
        className="mt-6 inline-flex rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-slate-deep"
      >
        Back to Academy
      </Link>
    </div>
  ),
});

function LessonReader() {
  const { slug } = Route.useLoaderData();
  const { profile } = useAuth();
  const tier = (profile?.tier ?? "free") as AcademyTier;

  const found = findLesson(slug);
  const { completed, toggle } = useLessonProgress();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  if (!found) return null;
  const { module, lesson, index } = found;

  if (!canAccessModule(tier, module.requiredTier)) {
    return <Navigate to="/dashboard/academy" />;
  }

  const isDone = completed.has(slug);
  const next = module.lessons[index + 1];

  // Academy-only overrides (title/excerpt/Content/toc) — do not affect public /education
  const override = getAcademyLessonOverride(slug);
  const displayTitle = override?.title ?? lesson.title;
  const displayExcerpt = override?.excerpt ?? lesson.excerpt;
  const displayToc = override?.toc ?? lesson.toc;
  const Content = useMemo(() => {
    if (override?.Content) return override.Content;
    return lazy(lesson.Content);
  }, [override, lesson]);

  // Upgrade teaser logic: free-tier user finishing a free-tier module while a higher-tier module exists next
  const userRank = TIER_RANK[tier] ?? 0;
  const nextLockedModule = modules.find(
    (m) => TIER_RANK[m.requiredTier] > userRank,
  );
  const showUpgradeTeaser =
    tier === "free" && module.requiredTier === "free" && Boolean(nextLockedModule);

  const handleMarkComplete = () => {
    void toggle(slug, !isDone);
  };

  return (
    <div className="min-h-full bg-slate-deep">
      {/* Slim top bar */}
      <div className="sticky top-0 z-20 border-b border-border/60 bg-slate-deep/85 backdrop-blur-xl">
        <div className="container mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Link
            to="/dashboard/academy"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition hover:text-gold"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to Academy
          </Link>
          <div className="hidden text-xs text-muted-foreground md:block">
            <span className="text-gold">Module {module.number}</span>
            <span className="mx-2">·</span>
            <span className="line-clamp-1">{lesson.title}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> {lesson.readTime}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
          <LessonReaderSidebar module={module} lesson={lesson} completed={completed} toc={displayToc} />

          <main>
            {/* Lesson title */}
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
              Chapter {String(index + 1).padStart(2, "0")} · {module.title}
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold leading-tight md:text-4xl">
              {displayTitle}
            </h1>
            <p className="mt-3 max-w-2xl text-base text-muted-foreground">
              {displayExcerpt}
            </p>

            {/* Reader body */}
            <div className="mx-auto mt-10 max-w-2xl">
              {!override && <KeyTakeaways items={lesson.keyTakeaways} />}
              <Suspense fallback={null}>
                <Content />
              </Suspense>
              {!override && <FAQ items={lesson.faq} />}
            </div>

            {/* Bottom controls */}
            <div className="mx-auto mt-12 flex max-w-2xl flex-col gap-3 border-t border-border/60 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <Button
                variant={isDone ? "outlineGold" : "gold"}
                onClick={handleMarkComplete}
              >
                {isDone ? (
                  <>
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Completed
                  </>
                ) : (
                  <>
                    <Circle className="mr-1 h-4 w-4" /> Mark as complete
                  </>
                )}
              </Button>

              {next ? (
                <Button asChild variant="ghost">
                  <Link
                    to="/dashboard/academy/$slug"
                    params={{ slug: next.slug }}
                  >
                    Next: {next.title.replace(/:.*$/, "")}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="ghost">
                  <Link to="/dashboard/academy">
                    Back to Academy <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>

            {/* Upgrade teaser for Free-tier users */}
            {showUpgradeTeaser && nextLockedModule && (
              <div className="mx-auto mt-8 max-w-2xl overflow-hidden rounded-xl border border-gold/40 bg-gradient-to-br from-gold-muted/40 via-card to-background p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                      {TIER_LABEL[nextLockedModule.requiredTier]} Plan · Module {nextLockedModule.number}
                    </div>
                    <h3 className="mt-1 font-display text-lg font-bold leading-snug">
                      Ready to master Liquidity?
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Upgrade to {TIER_LABEL[nextLockedModule.requiredTier]} to unlock{" "}
                      <span className="text-foreground/90">{nextLockedModule.title}</span>{" "}
                      and continue your journey to professional execution.
                    </p>
                    <Button asChild variant="gold" size="sm" className="mt-4">
                      <Link to="/pricing">
                        Upgrade Plan <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
